import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Empty,
  Form,
  Input,
  Radio,
  Select,
  Spin,
  Tag,
} from "antd";
import {
  CameraOutlined,
  LinkOutlined,
  MailOutlined,
  MobileOutlined,
  ShopOutlined,
  UploadOutlined,
  UserOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import PhoneInput from "react-phone-input-2";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import InsideHeader from "../../components/InsideHeader";
import { saveFile } from "../../features/save/saveSlice";
import { fetchResellerAccounts } from "../../features/reseller-account/resellerAccountSlice";
import {
  fetchWhatsAppRegistrations,
  fetchWhatsAppShortcodes,
  saveWhatsAppRegistration,
} from "../../features/whatsapp/whatsappSlice";
import { formatImgPath, getLetterWord } from "../../utils";
import {
  extractErrorMessage,
  formatWhatsAppDate,
  normalizeRecipient,
} from "./whatsappUtils";
import "./whatsapp.css";

const { TextArea } = Input;

const categoryOptions = [
  "UTILITY",
  "CUSTOMER_SUPPORT",
  "BILLING",
  "FINANCE",
  "EDUCATION",
  "HEALTHCARE",
  "RETAIL",
  "LOGISTICS",
  "GOVERNMENT",
  "OTHER",
];

const displayNameOptions = [
  {
    value: "SMS_SHORTCODE",
    title: "Use SMS shortcode",
    description: "Mirror an existing SMS sender name for consistent brand identity.",
  },
  {
    value: "CUSTOM",
    title: "Type a new display name",
    description: "Enter a WhatsApp-specific name when the sender should look different.",
  },
];

const WhatsAppNumbers = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth || {});
  const { resellerAccountData } = useSelector((state) => state.resellerAccount || {});
  const whatsappState = useSelector((state) => state.whatsapp || {});
  const {
    loadingRegistrations,
    loadingShortcodes,
    registrations = [],
    shortcodes = [],
    savingRegistration,
  } = whatsappState;
  const [phoneNumber, setPhoneNumber] = useState("");
  const [profilePicture, setProfilePicture] = useState("");
  const [profilePreview, setProfilePreview] = useState("");
  const [uploadingPicture, setUploadingPicture] = useState(false);

  const isAccountUser = user?.layer === "ACCOUNT";
  const isResellerUser = user?.layer === "RESELLER";
  const displayNameMode = Form.useWatch("displayNameMode", form) || "SMS_SHORTCODE";
  const customDisplayName = Form.useWatch("customDisplayName", form);
  const smsShortcode = Form.useWatch("smsShortcode", form);
  const selectedAccountId = Form.useWatch("accountId", form);

  useEffect(() => {
    form.setFieldsValue({ displayNameMode: "SMS_SHORTCODE" });
  }, [form]);

  useEffect(() => {
    dispatch(fetchWhatsAppShortcodes());
  }, [dispatch]);

  useEffect(() => {
    if (!isAccountUser) {
      dispatch(fetchResellerAccounts());
    }
  }, [dispatch, isAccountUser]);

  useEffect(() => {
    const params = !isAccountUser && selectedAccountId ? { accountId: selectedAccountId } : undefined;
    dispatch(fetchWhatsAppRegistrations(params));
  }, [dispatch, isAccountUser, selectedAccountId]);

  const accountOptions = useMemo(
    () =>
      (Array.isArray(resellerAccountData) ? resellerAccountData : []).map((account) => ({
        label: account?.accName,
        value: account?.accId,
      })),
    [resellerAccountData]
  );

  const registrationsList = Array.isArray(registrations) ? registrations : [];
  const shortcodeList = Array.isArray(shortcodes) ? shortcodes : [];

  const displayNamePreview =
    displayNameMode === "CUSTOM"
      ? String(customDisplayName || "").trim() || "Custom display name"
      : String(smsShortcode || "").trim() || "SMS shortcode";

  const handlePictureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const localPreview = URL.createObjectURL(file);
    setProfilePreview(localPreview);
    setUploadingPicture(true);

    try {
      const uploadData = new FormData();
      uploadData.append("file", file);
      const result = await dispatch(saveFile(uploadData));
      if (result?.payload?.success) {
        setProfilePicture(result?.payload?.data?.result ?? "");
        toast.success("Profile picture uploaded");
      } else {
        setProfilePicture("");
        toast.error(result?.payload?.messages?.message || "Unable to upload picture");
      }
    } catch (error) {
      setProfilePicture("");
      toast.error("Unable to upload picture");
    } finally {
      setUploadingPicture(false);
    }
  };

  useEffect(() => {
    return () => {
      if (profilePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(profilePreview);
      }
    };
  }, [profilePreview]);

  const onFinish = async (values) => {
    const normalizedPhone = normalizeRecipient(phoneNumber);
    if (normalizedPhone.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }

    const payload = {
      accountId: isAccountUser ? undefined : values.accountId,
      phoneNumber: normalizedPhone,
      profilePicture,
      displayNameMode: values.displayNameMode,
      smsShortcode: values.smsShortcode,
      customDisplayName: values.customDisplayName,
      category: values.category,
      description: values.description,
      address: values.address,
      email: values.email,
      website: values.website,
    };

    const result = await dispatch(saveWhatsAppRegistration(payload));
    if (saveWhatsAppRegistration.fulfilled.match(result)) {
      toast.success(result.payload?.messages?.message || "WhatsApp sender number saved");
      const accountId = isAccountUser ? undefined : values.accountId;
      form.resetFields();
      form.setFieldsValue({
        accountId,
        displayNameMode: "SMS_SHORTCODE",
      });
      setPhoneNumber("");
      setProfilePicture("");
      setProfilePreview("");
      dispatch(fetchWhatsAppRegistrations(accountId ? { accountId } : undefined));
      return;
    }

    toast.error(extractErrorMessage(result.payload));
  };

  return (
    <div className="w-full whatsapp-page">
      <InsideHeader
        title="WhatsApp Numbers"
        subtitle="Register sender identities and business details for outbound WhatsApp messaging"
        back
        compact
      />

      <div className="whatsapp-shell whatsapp-send-shell">
        <section className="whatsapp-panel whatsapp-send-panel">
          <div className="whatsapp-send-hero">
            <div>
              <div className="whatsapp-kicker">Sender registration</div>
              <h1>Register WhatsApp sender numbers</h1>
              <p>
                Add the number, brand it with a shortcode or custom display name, then
                store the business profile details the operations team will use later.
              </p>
            </div>

            <div className="whatsapp-send-hero-pills">
              <span className="whatsapp-send-pill">
                <WhatsAppOutlined />
                Registered {registrationsList.length}
              </span>
              <span className="whatsapp-send-pill">
                <ShopOutlined />
                Shortcodes {shortcodeList.length}
              </span>
            </div>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              displayNameMode: "SMS_SHORTCODE",
            }}
          >
            <div className="whatsapp-registration-grid">
              <div className="whatsapp-send-column">
                <section className="whatsapp-send-section">
                  <div className="whatsapp-send-section-heading">
                    <div>
                      <h2>Phone identity</h2>
                      <p>Register the WhatsApp number and the brand presentation attached to it.</p>
                    </div>
                    <Tag color="green">Required</Tag>
                  </div>

                  {!isAccountUser && (
                    <Form.Item
                      name="accountId"
                      label="Account"
                      rules={[{ required: true, message: "Please choose an account" }]}
                    >
                      <Select
                        showSearch
                        placeholder="Select account"
                        options={accountOptions}
                        optionFilterProp="label"
                        className="whatsapp-filter-control"
                      />
                    </Form.Item>
                  )}

                  <Form.Item
                    label="Phone number"
                    required
                    validateStatus={normalizeRecipient(phoneNumber).length >= 10 || phoneNumber.length === 0 ? "" : "error"}
                    help={normalizeRecipient(phoneNumber).length >= 10 || phoneNumber.length === 0 ? null : "Enter a valid WhatsApp number"}
                  >
                    <PhoneInput
                      country="ke"
                      countryCodeEditable={false}
                      inputClass="whatsapp-phone-input"
                      containerClass="whatsapp-phone"
                      value={phoneNumber}
                      onChange={(value) => setPhoneNumber(String(value))}
                    />
                  </Form.Item>

                  <div className="whatsapp-registration-picture-row">
                    <div className="whatsapp-registration-avatar">
                      {profilePreview || profilePicture ? (
                        <img
                          src={profilePreview || formatImgPath(profilePicture)}
                          alt="WhatsApp sender"
                        />
                      ) : (
                        <span>
                          <CameraOutlined />
                        </span>
                      )}
                    </div>

                    <div className="whatsapp-registration-picture-copy">
                      <strong>Profile picture</strong>
                      <span>Optional. Upload a logo or brand mark for the registered number.</span>
                      <label className="whatsapp-upload-dropzone whatsapp-registration-upload">
                        <input type="file" accept="image/*" onChange={handlePictureUpload} />
                        <span className="whatsapp-upload-dropzone-icon">
                          {uploadingPicture ? <Spin size="small" /> : <UploadOutlined />}
                        </span>
                        <span className="whatsapp-upload-dropzone-copy">
                          <strong>{profilePicture ? "Replace picture" : "Upload picture"}</strong>
                          <span>{profilePicture ? "Image uploaded successfully" : "Choose a logo or profile image"}</span>
                        </span>
                      </label>
                    </div>
                  </div>

                  <Form.Item name="displayNameMode" label="Display name option" className="whatsapp-registration-display-mode">
                    <Radio.Group className="whatsapp-display-radio-group">
                      {displayNameOptions.map((option) => (
                        <Radio.Button key={option.value} value={option.value} className="whatsapp-display-radio">
                          <strong>{option.title}</strong>
                          <span>{option.description}</span>
                        </Radio.Button>
                      ))}
                    </Radio.Group>
                  </Form.Item>

                  {displayNameMode === "SMS_SHORTCODE" ? (
                    <Form.Item
                      name="smsShortcode"
                      label="SMS shortcode"
                      rules={[{ required: true, message: "Please choose an SMS shortcode" }]}
                    >
                      <Select
                        loading={loadingShortcodes}
                        placeholder={shortcodeList.length > 0 ? "Select shortcode" : "No shortcode available yet"}
                        options={shortcodeList.map((item) => ({ label: item, value: item }))}
                        className="whatsapp-filter-control"
                      />
                    </Form.Item>
                  ) : (
                    <Form.Item
                      name="customDisplayName"
                      label="Custom display name"
                      rules={[{ required: true, message: "Please type a display name" }]}
                    >
                      <Input placeholder="Smartgate Support" />
                    </Form.Item>
                  )}

                  {displayNameMode === "SMS_SHORTCODE" && shortcodeList.length === 0 && (
                    <Alert
                      type="warning"
                      showIcon
                      message="No SMS shortcode is mapped yet"
                      description="You can still switch to custom display name, or map a sender ID first and come back."
                    />
                  )}

                  <div className="whatsapp-template-live-card">
                    <span className="whatsapp-template-live-label">Display name preview</span>
                    <strong>{displayNamePreview}</strong>
                    <p>This is the name operators will associate with the registered sender identity.</p>
                  </div>
                </section>
              </div>

              <div className="whatsapp-send-column">
                <section className="whatsapp-send-section">
                  <div className="whatsapp-send-section-heading">
                    <div>
                      <h2>Business information</h2>
                      <p>Capture the customer-facing business details linked to this WhatsApp number.</p>
                    </div>
                  </div>

                  <div className="whatsapp-registration-fields">
                    <Form.Item
                      name="category"
                      label="Category"
                      rules={[{ required: true, message: "Please choose a category" }]}
                    >
                      <Select
                        placeholder="Select category"
                        options={categoryOptions.map((item) => ({ label: item.replaceAll("_", " "), value: item }))}
                      />
                    </Form.Item>

                    <Form.Item name="email" label="Email">
                      <Input placeholder="support@company.com" />
                    </Form.Item>

                    <Form.Item name="website" label="Website">
                      <Input placeholder="https://company.com" />
                    </Form.Item>

                    <Form.Item name="address" label="Address">
                      <Input placeholder="Nairobi, Kenya" />
                    </Form.Item>
                  </div>

                  <Form.Item name="description" label="Description">
                    <TextArea
                      rows={5}
                      className="input-textarea"
                      placeholder="A short note about what this registered WhatsApp number is used for."
                    />
                  </Form.Item>

                  <div className="whatsapp-send-context-note">
                    <UserOutlined />
                    <span>
                      Keep the name and business details close to what customers already
                      recognize from SMS or support channels.
                    </span>
                  </div>

                  <div className="whatsapp-actions whatsapp-send-submit-row">
                    <Button
                      htmlType="submit"
                      type="primary"
                      className="whatsapp-submit whatsapp-send-submit"
                      disabled={savingRegistration || uploadingPicture}
                    >
                      {savingRegistration ? <Spin size="small" /> : "Save WhatsApp Number"}
                    </Button>
                  </div>
                </section>
              </div>
            </div>
          </Form>
        </section>

        <section className="whatsapp-panel whatsapp-registration-list-panel">
          <div className="whatsapp-panel-title-row">
            <div>
              <h2>Registered WhatsApp numbers</h2>
              <p>Review the sender identities already stored for WhatsApp operations.</p>
            </div>
            {!isAccountUser && isResellerUser && (
              <Tag color="blue">{selectedAccountId ? "Filtered by account" : "Showing all accounts"}</Tag>
            )}
          </div>

          {loadingRegistrations ? (
            <div className="whatsapp-registration-loading">
              <Spin />
            </div>
          ) : registrationsList.length === 0 ? (
            <div className="whatsapp-empty-result whatsapp-registration-empty">
              <Empty description="No WhatsApp numbers registered yet" />
            </div>
          ) : (
            <div className="whatsapp-registration-card-grid">
              {registrationsList.map((item) => {
                const picture = item?.profilePicture ? formatImgPath(item.profilePicture) : null;
                const initials = getLetterWord(item?.displayName || item?.accountName || "WA").slice(0, 2);

                return (
                  <article key={item?.id} className="whatsapp-registration-card">
                    <div className="whatsapp-registration-card-top">
                      <div className="whatsapp-registration-card-avatar">
                        {picture ? (
                          <img src={picture} alt={item?.displayName} />
                        ) : (
                          <span>{initials}</span>
                        )}
                      </div>

                      <div className="whatsapp-registration-card-title">
                        <div className="whatsapp-registration-card-heading">
                          <h3>{item?.displayName}</h3>
                          <Tag color={item?.displayNameMode === "SMS_SHORTCODE" ? "blue" : "green"}>
                            {item?.displayNameMode === "SMS_SHORTCODE" ? "SMS shortcode" : "Custom"}
                          </Tag>
                        </div>
                        <p>{item?.phoneNumber}</p>
                        {!isAccountUser && item?.accountName && (
                          <span className="whatsapp-registration-account-tag">{item.accountName}</span>
                        )}
                      </div>
                    </div>

                    <div className="whatsapp-registration-business-grid">
                      <div>
                        <span>
                          <ShopOutlined />
                          Category
                        </span>
                        <strong>{item?.category?.replaceAll("_", " ") || "-"}</strong>
                      </div>

                      <div>
                        <span>
                          <MobileOutlined />
                          Phone
                        </span>
                        <strong>{item?.phoneNumber || "-"}</strong>
                      </div>

                      <div>
                        <span>
                          <MailOutlined />
                          Email
                        </span>
                        <strong>{item?.email || "-"}</strong>
                      </div>

                      <div>
                        <span>
                          <LinkOutlined />
                          Website
                        </span>
                        <strong>{item?.website || "-"}</strong>
                      </div>
                    </div>

                    {(item?.description || item?.address || item?.smsShortcode) && (
                      <div className="whatsapp-registration-notes">
                        {item?.smsShortcode && (
                          <Tag color="cyan">Shortcode: {item.smsShortcode}</Tag>
                        )}
                        {item?.address && <p><strong>Address:</strong> {item.address}</p>}
                        {item?.description && <p><strong>Description:</strong> {item.description}</p>}
                      </div>
                    )}

                    <div className="whatsapp-registration-footer">
                      <span>Updated {formatWhatsAppDate(item?.updatedAt)}</span>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default WhatsAppNumbers;
